import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddCertificationProviders,
    CreateCertificationProvider,
    CreateCertificationProviderFailure,
    CreateCertificationProviderSuccess,
    DeleteCertificationProvider,
    DeleteCertificationProviderFailure,
    DeleteCertificationProviderSuccess,
    FetchCertificationProvider,
    FetchCertificationProviderFailure,
    FetchCertificationProviderSuccess,
    ReplaceCertificate,
    ReplaceCertificateFailure,
    ReplaceCertificateSuccess,
    SetCertificationProvider,
    SetCertificationProviders,
    UnsetCertificationProvider,
    UpdateCertificationProvider,
    UpdateCertificationProviderFailure,
    UpdateCertificationProviderStatus,
    UpdateCertificationProviderStatusFailure,
    UpdateCertificationProviderStatusSuccess,
    UpdateCertificationProviderSuccess,
} from '../actions';
import { CertificationProviderApiService } from '../services/certification-provider.api-service';
import { CertificationProviderStateModel } from './certification-provider.state-model';

@State<CertificationProviderStateModel>({
    name: 'certificationProvider',
    defaults: new CertificationProviderStateModel(),
})
@Injectable()
export class CertificationProviderState extends EntityState<CertificationProvider> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: CertificationProviderStateModel, id: string = state.item): CertificationProvider {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CertificationProviderStateModel): CertificationProvider[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(CertificationProviderState.sortProperties));

        return toReturn;
    }

    constructor(private _certificationProviderService: CertificationProviderApiService) {
        super(null, 30);
    }

    @Action(AddCertificationProviders)
    addEntities(ctx: StateContext<CertificationProviderStateModel>, action: AddCertificationProviders): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCertificationProvider)
    setEntity(ctx: StateContext<CertificationProviderStateModel>, action: SetCertificationProvider): void {
        this._setEntity(ctx, action, AddCertificationProviders);
    }

    @Action(UnsetCertificationProvider)
    unsetEntity(ctx: StateContext<CertificationProviderStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCertificationProvider)
    createEntity(ctx: StateContext<CertificationProviderStateModel>, action: CreateCertificationProvider): void {
        this._certificationProviderService.create(action.payload.name, action.payload.type).subscribe(
            (entity) => {
                ctx.dispatch([new AddCertificationProviders([entity]), new CreateCertificationProviderSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(
                    new CreateCertificationProviderFailure({
                        response: error,
                    })
                );
            }
        );
    }

    @Action(ReplaceCertificate)
    replaceCertificate(ctx: StateContext<CertificationProviderStateModel>, action: ReplaceCertificate): void {
        this._certificationProviderService.replaceCertificate(action.payload.certificateId, action.payload.certificationId).subscribe(
            (entity) => {
                ctx.dispatch(new ReplaceCertificateSuccess());
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new ReplaceCertificateFailure());
            }
        );
    }

    @Action(UpdateCertificationProvider)
    updateEntity(ctx: StateContext<CertificationProviderStateModel>, action: UpdateCertificationProvider): void {
        this._certificationProviderService.update(action.payload.name, action.payload.id).subscribe(
            (entity) => {
                ctx.dispatch([new AddCertificationProviders([entity]), new UpdateCertificationProviderSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UpdateCertificationProviderFailure({ response: error }));
            }
        );
    }

    @Action(UpdateCertificationProviderStatus)
    updateCertificationProviderStatus(ctx: StateContext<CertificationProviderStateModel>, action: UpdateCertificationProviderStatus): void {
        this._certificationProviderService.updateStatus(action.payload.id, action.payload.status).subscribe(
            (entity) => {
                ctx.dispatch([new AddCertificationProviders([entity]), new UpdateCertificationProviderStatusSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UpdateCertificationProviderStatusFailure());
            }
        );
    }

    @Action(DeleteCertificationProvider)
    deleteEntity(ctx: StateContext<CertificationProviderStateModel>, action: DeleteCertificationProvider): void {
        this._certificationProviderService.delete(action.payload).subscribe(
            () => {
                ctx.dispatch(new DeleteCertificationProviderSuccess(action.payload));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new DeleteCertificationProviderFailure(action.payload));
            }
        );
    }

    @Action(FetchCertificationProvider)
    fetchEntity(ctx: StateContext<CertificationProviderStateModel>, action: FetchCertificationProvider): void {
        this._fetchEntity(ctx, action, FetchCertificationProviderSuccess, FetchCertificationProviderFailure, AddCertificationProviders, SetCertificationProvider).then(() => {});
    }

    @Action(FetchCertificationProviderSuccess)
    fetchEntitySuccess(ctx: StateContext<CertificationProviderStateModel>, action: FetchCertificationProviderSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCertificationProviderFailure)
    fetchEntityFailure(ctx: StateContext<CertificationProviderStateModel>, action: FetchCertificationProviderFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCertificationProviders)
    setEntities(ctx: StateContext<CertificationProviderStateModel>, action: SetCertificationProviders): void {
        this._setEntities(ctx, action);
    }
}
